import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Button,
    Box,
    VStack,
    Text,
    Flex,
    Input,
    Spinner,
    useToast,
    Divider,
    IconButton,
    HStack,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
} from "@chakra-ui/react";
import { useState, useContext, useEffect } from "react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import moment from "moment";

const CrmCostDrawer = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { toastNotify } = useContext(AppContext);
    const toast = useToast();
    const [costs, setCosts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [newCostName, setNewCostName] = useState("");
    const [newProfitCenter, setNewProfitCenter] = useState("");
    const [editingCost, setEditingCost] = useState<any>(null);
    const [editName, setEditName] = useState("");
    const [editProfitCenter, setEditProfitCenter] = useState("");

    const fetchCosts = async () => {
        try {
            setLoading(true);
            const response: any = await appRequest("crmCost", "getall", {});
            if (response?.status === "success") {
                setCosts(Array.isArray(response.data) ? response.data : []);
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to fetch costs",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Fetch costs error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchCosts();
        }
    }, [isOpen]);

    const handleAddCost = async () => {
        if (!newCostName.trim()) {
            toast({
                title: "Cost name is required",
                status: "warning",
                duration: 2000,
            });
            return;
        }

        // Duplicate check
        const exists = costs.find(c => 
            c.costName === newCostName.trim() && 
            (c.profitCenter || "") === (newProfitCenter.trim() || "")
        );

        if (exists) {
            toast({
                title: "Duplicate entry",
                description: "This Cost Center and Profit Center combination already exists.",
                status: "error",
                duration: 3000,
            });
            return;
        }

        try {
            setAddLoading(true);
            const response: any = await appRequest("crmCost", "create", {
                processName: "crm",
                costName: newCostName.trim(),
                profitCenter: newProfitCenter.trim(),
                companyCode: "crm" // Default company code if needed, or get from context
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Cost added successfully",
                    type: "success",
                });
                setNewCostName("");
                setNewProfitCenter("");
                fetchCosts();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to add cost",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Add cost error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleUpdateCost = async () => {
        if (!editName.trim() || !editingCost?._id) return;

        try {
            setAddLoading(true);
            const response: any = await appRequest("crmCost", "update", {
                id: editingCost._id,
                costName: editName.trim(),
                profitCenter: editProfitCenter.trim(),
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Cost updated successfully",
                    type: "success",
                });
                setEditingCost(null);
                fetchCosts();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to update cost",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Update cost error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleDeleteCost = async (id: string) => {
        if (!id) return;
        try {
            const response: any = await appRequest("crmCost", "delete", { id });
            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Cost deleted successfully",
                    type: "success",
                });
                fetchCosts();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to delete cost",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Delete cost error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        }
    };

    const DEFAULT_CRM_COSTS = [
        { profitCenter: "0000005000", costName: "5015" },
        { profitCenter: "CNS", costName: "4110-EBT" },
        { profitCenter: "CONS_GBL", costName: "4109-EBT" },
        { profitCenter: "CONSINA", costName: "4128" },
        { profitCenter: "CRITI_CARE", costName: "4126" },
        { profitCenter: "CUTIX", costName: "4138" },
        { profitCenter: "ECD001", costName: "4138" },
        { profitCenter: "ECD001", costName: "ECD001_001" },
        { profitCenter: "EMD001", costName: "4134" },
        { profitCenter: "EMD002", costName: "4153" },
        { profitCenter: "EMD004", costName: "4155" },
        { profitCenter: "EMD004", costName: "4162" },
        { profitCenter: "EMD005", costName: "4156" },
        { profitCenter: "EMD006", costName: "4157" },
        { profitCenter: "EMD006", costName: "4157_01" },
        { profitCenter: "EMD007", costName: "4158" },
        { profitCenter: "EMD007", costName: "4158_01" },
        { profitCenter: "EMD007", costName: "4158_02" },
        { profitCenter: "EMD010", costName: "4162" },
        { profitCenter: "EMD011", costName: "4163" },
        { profitCenter: "EMD012", costName: "4164" },
        { profitCenter: "EMD012", costName: "4164_02" },
        { profitCenter: "EMKART", costName: "4151" },
        { profitCenter: "EMKART", costName: "4151_01" },
        { profitCenter: "EMKART", costName: "4151_02" },
        { profitCenter: "ENNOVA", costName: "4102" },
        { profitCenter: "EPL_RENAL", costName: "4111" },
        { profitCenter: "EXPORTS", costName: "4201" },
        { profitCenter: "GBL_GOV_IN", costName: "4107-EBT" },
        { profitCenter: "GEN_NEPHRO", costName: "4112-EBT" },
        { profitCenter: "GENNOVA", costName: "4104-EBT" },
        { profitCenter: "GMD001", costName: "4118_EBT" },
        { profitCenter: "GMD003", costName: "4120_EBT" },
        { profitCenter: "GMD005", costName: "4122_EBT" },
        { profitCenter: "GOV_INST_S", costName: "4107-EBT" },
        { profitCenter: "GOV_INST_S", costName: "4132" },
        { profitCenter: "INST_SALES", costName: "4131" },
        { profitCenter: "INST_SALES", costName: "4161" },
        { profitCenter: "INVENTIA", costName: "4149" },
        { profitCenter: "KONKER", costName: "4108" },
        { profitCenter: "MKT COMMON", costName: "4104" },
        { profitCenter: "MKT COMMON", costName: "4199" },
        { profitCenter: "NEPAL", costName: "4119" },
        { profitCenter: "NIFIREX", costName: "4123" },
        { profitCenter: "NUCRON", costName: "4104" },
        { profitCenter: "NUCRON CV", costName: "4127" },
        { profitCenter: "NUCRON CV", costName: "4127_01" },
        { profitCenter: "ONCO_SPE_E", costName: "4140" },
        { profitCenter: "ONCO_SPE_G", costName: "4111-EBT" },
        { profitCenter: "ONCO_SPE_G", costName: "4140" },
        { profitCenter: "ONCOCARE", costName: "4140" },
        { profitCenter: "PHARMA", costName: "4105" },
        { profitCenter: "TASPIANCE", costName: "4111" },
        { profitCenter: "TASPIANCE", costName: "4129" },
        { profitCenter: "XENNEA", costName: "4121" },
        { profitCenter: "XENNEX", costName: "4122" },
        { profitCenter: "XENNEX", costName: "4122_0001" },
    ];

    const handleSeedDefaults = async () => {
        try {
            setAddLoading(true);
            let count = 0;
            for (const item of DEFAULT_CRM_COSTS) {
                // Check if already exists to avoid duplicates (optional but safer)
                const exists = costs.find(c => c.costName === item.costName && c.profitCenter === item.profitCenter);
                if (exists) continue;

                await appRequest("crmCost", "create", {
                    processName: "crm",
                    costName: item.costName,
                    profitCenter: item.profitCenter,
                    companyCode: "crm"
                });
                count++;
            }
            toastNotify({
                title: "Success",
                message: `${count} default costs added successfully`,
                type: "success",
            });
            fetchCosts();
        } catch (error: any) {
            console.error("Seed costs error:", error);
            toastNotify({
                title: "Error",
                message: "Failed to seed some costs",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
            <DrawerOverlay backdropFilter="blur(5px)" bg="rgba(0,0,0,0.1)" />
            <DrawerContent maxW="80vw" borderRadius="2xl" boxShadow="2xl">
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px" pb={2}>
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Text fontSize="xl" fontWeight="800" color="teal.500">
                                CRM Costs
                            </Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                                Manage costs for the CRM process
                            </Text>
                        </Box>
                        <Button
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            onClick={handleSeedDefaults}
                            isLoading={addLoading}
                            leftIcon={<AddIcon />}
                            borderRadius="lg"
                        >
                            Seed Defaults
                        </Button>
                    </Flex>
                </DrawerHeader>

                <DrawerBody>
                    <VStack spacing={6} align="stretch" py={4}>
                        <Box bg="teal.50" p={4} borderRadius="xl" border="1px solid" borderColor="teal.100">
                            <Text fontSize="sm" fontWeight="700" mb={3} color="teal.700">
                                {editingCost ? "Update Cost" : "Add New Cost"}
                            </Text>
                            <VStack spacing={3}>
                                <HStack w="full">
                                    <Input
                                        placeholder="Cost name (e.g. Finance, IT)"
                                        value={editingCost ? editName : newCostName}
                                        onChange={(e) => editingCost ? setEditName(e.target.value) : setNewCostName(e.target.value)}
                                        bg="white"
                                        borderRadius="lg"
                                        fontSize="sm"
                                        _focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px teal.400" }}
                                    />
                                    <Input
                                        placeholder="Profit Center (Optional)"
                                        value={editingCost ? editProfitCenter : newProfitCenter}
                                        onChange={(e) => editingCost ? setEditProfitCenter(e.target.value) : setNewProfitCenter(e.target.value)}
                                        bg="white"
                                        borderRadius="lg"
                                        fontSize="sm"
                                        _focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px teal.400" }}
                                    />
                                </HStack>
                                <HStack w="full" justify="flex-end">
                                    {editingCost && (
                                        <Button size="sm" variant="ghost" onClick={() => setEditingCost(null)}>
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        colorScheme="teal"
                                        onClick={editingCost ? handleUpdateCost : handleAddCost}
                                        isLoading={addLoading}
                                        leftIcon={editingCost ? <EditIcon /> : <AddIcon />}
                                        borderRadius="lg"
                                        px={6}
                                        fontSize="sm"
                                    >
                                        {editingCost ? "Update" : "Add"}
                                    </Button>
                                </HStack>
                            </VStack>
                        </Box>

                        <Divider />

                        <Box>
                            <Text fontSize="sm" fontWeight="700" mb={4} color="gray.700">
                                Existing Costs
                            </Text>
                            {loading ? (
                                <Flex justify="center" align="center" py={10}>
                                    <Spinner color="teal.500" />
                                </Flex>
                            ) : costs.length > 0 ? (
                                <TableContainer border="1px solid" borderColor="gray.100" borderRadius="xl" bg="gray.50">
                                    <Table variant="simple" size="sm">
                                        <Thead bg="gray.100">
                                            <Tr>
                                                <Th py={4}>Cost Name</Th>
                                                <Th py={4}>Profit Center</Th>
                                                <Th py={4}>Process</Th>
                                                <Th py={4}>Created By</Th>
                                                <Th py={4}>Created At</Th>
                                                <Th py={4} textAlign="right">Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {costs.map((cost) => (
                                                <Tr
                                                    key={cost._id}
                                                    _hover={{ bg: "white" }}
                                                    transition="all 0.2s"
                                                >
                                                    <Td>
                                                        <Text fontWeight="600">{cost.costName}</Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="sm" fontWeight="500">{cost.profitCenter || "N/A"}</Text>
                                                    </Td>
                                                    <Td>
                                                        <Badge variant="outline" colorScheme="blue" borderRadius="md">
                                                            {cost.processName}
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.600">
                                                            {cost.createdBy?.name || cost.createdBy?.username || "System"}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.600">
                                                            {cost.created_At ? moment(cost.created_At).format("DD-MM-YYYY HH:mm") : "N/A"}
                                                        </Text>
                                                    </Td>
                                                    <Td textAlign="right">
                                                        <HStack spacing={1} justify="flex-end">
                                                            <IconButton
                                                                aria-label="Edit cost"
                                                                icon={<EditIcon />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="blue"
                                                                onClick={() => {
                                                                    setEditingCost(cost);
                                                                    setEditName(cost.costName);
                                                                    setEditProfitCenter(cost.profitCenter || "");
                                                                }}
                                                            />
                                                            <IconButton
                                                                aria-label="Delete cost"
                                                                icon={<DeleteIcon />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="red"
                                                                onClick={() => handleDeleteCost(cost._id)}
                                                            />
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Flex direction="column" align="center" justify="center" py={10} color="gray.400">
                                    <Text fontSize="sm">No costs found</Text>
                                </Flex>
                            )}
                        </Box>
                    </VStack>
                </DrawerBody>

                <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={onClose} borderRadius="lg">
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export default CrmCostDrawer;
